import * as XLSX from "xlsx";

/**
 * Turn date-shaped TEXT cells into real Excel date cells, in place.
 *
 * Every API in this app formats dates server-side into strings ("13-Aug-2026 14:30",
 * "13-08-2026", "2026-08-13"), so exports land in Excel as text: they sort
 * alphabetically, can't be filtered by "last month", and don't work in formulas.
 *
 * Matching is deliberately STRICT — the whole cell must be a date and nothing else — so
 * identifiers that merely contain digits and dashes (auction ids like
 * "MFSLUP-20260818-0930-37500", reference numbers, phone numbers) are left untouched.
 *
 * Day-first is assumed for the numeric form, matching the backend's "%d-%m-%Y".
 */
const PATTERNS = [
  // 13-Aug-2026 [14:30[:05]]  /  13 Aug 2026
  {
    re: /^(\d{1,2})[-\s]([A-Za-z]{3})[-\s](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
    build: (m) => [+m[3], MONTHS[m[2].toLowerCase()], +m[1], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0)],
    monthFromName: true,
  },
  // 13-08-2026 [14:30[:05]]  (day first)
  {
    re: /^(\d{1,2})-(\d{1,2})-(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
    build: (m) => [+m[3], +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0)],
  },
  // 2026-08-13 [14:30[:05]]  (ISO)
  {
    re: /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
    build: (m) => [+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0)],
  },
];

const MONTHS = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

const DATE_FMT = "dd-mmm-yyyy";

// Whole days since the 1899-12-30 epoch Excel counts from. Built from the calendar
// fields so no timezone offset can leak in and shift the day.
const toExcelSerial = (d) =>
  Math.round(
    (Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - Date.UTC(1899, 11, 30)) /
      86400000,
  );

// Returns { d: Date, hasTime: boolean } or null.
export const parseDateCell = (text) => {
  const v = String(text || "").trim();
  if (!v || v.length > 25) return null;
  for (const p of PATTERNS) {
    const m = p.re.exec(v);
    if (!m) continue;
    if (p.monthFromName && MONTHS[m[2].toLowerCase()] === undefined) continue;
    const [y, mo, d, hh, mi, ss] = p.build(m);
    if (mo == null || mo < 0 || mo > 11 || d < 1 || d > 31) return null;
    const dt = new Date(y, mo, d, hh, mi, ss);
    // reject impossible dates that JS would roll over (31 Feb -> 3 Mar)
    if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
    return { d: dt, hasTime: m[4] !== undefined };
  }
  return null;
};

/** Rewrite every date-shaped string cell of a worksheet as a real date cell. */
export const coerceDateCells = (ws) => {
  if (!ws || !ws["!ref"]) return ws;
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[addr];
      if (!cell || cell.t !== "s" || typeof cell.v !== "string") continue;
      const hit = parseDateCell(cell.v);
      if (!hit) continue;
      // Date-ONLY cells are converted. A value carrying a time is left as text on
      // purpose: a real datetime cell drags a timezone-sensitive time component into
      // the sheet, and these strings are already formatted for reading. Only the plain
      // dates gain anything from being real dates (sorting, filtering, formulas).
      if (hit.hasTime) continue;
      // Written as a WHOLE-DAY Excel serial with a date format, which is exactly how
      // Excel stores a date natively. Handing SheetJS a JS Date instead leaves a few
      // seconds of fractional drift on the serial (46247.000115 rather than 46247) —
      // invisible on screen, but it makes =A2=DATE(2026,8,13) come out false.
      cell.t = "n";
      cell.v = toExcelSerial(hit.d);
      cell.z = DATE_FMT;
      delete cell.w;   // drop the cached text, or Excel shows the old string
    }
  }
  return ws;
};
